/**
 * Query hooks for fetching notes
 *
 * Wraps the query options from api/queries/notes.ts into
 * convenient hooks for component consumption.
 */

import {
  notesInfiniteQueryOptions,
  notesListQueryOptions,
} from '@/api/queries/notes';
import { ListNotesParams } from '@/api/endpoints/notes';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

/**
 * Hook to fetch paginated notes with infinite scrolling
 *
 * @param params - Optional filtering parameters (business_id, menu_item_id, etc.)
 * @returns Infinite query result with paginated notes
 */
export function useMyNotes(params: Omit<ListNotesParams, 'page'> = {}) {
  return useInfiniteQuery(notesInfiniteQueryOptions(params));
}

/**
 * Hook to fetch notes for a specific menu item
 *
 * @param menuItemId - Menu item ID to filter by
 * @returns Query result with notes list
 */
export function useNotesByMenuItem(menuItemId: string) {
  return useQuery(
    notesListQueryOptions({ menu_item_id: menuItemId })
  );
}

/**
 * Hook to fetch notes for a specific business
 *
 * @param businessId - Business ID to filter by
 * @returns Query result with notes list
 */
export function useNotesByBusiness(businessId: string) {
  return useQuery(
    notesListQueryOptions({ business_id: businessId })
  );
}
